import {Box, IconButton, Typography} from '@mui/material'
import {DOMAttributes, FC, useMemo} from 'react'
import {useTranslate} from 'react-admin'
import {Link} from 'react-router-dom'
import {makeStyles} from 'tss-react/mui'

import {useCompileChatMessageText} from '../../../hooks/useCompileChatMessageText'
import {useDelayRemoveChatMessageIdSearchParam} from '../../../hooks/useSearchParams'
import {useUserDisplayName} from '../../../hooks/useUserDisplayName'
import hexToRgb from '../../../theme/hexToRgb'
import {ChatMessages} from '../../../types/graphqlSchema'
import {CloseIcon} from '../../icons'
import LineClamp from '../../LineClamp'
import ChatMessageFileAttachment from './ChatMessageFileAttachment'

const RepliedChatMessageBanner: FC<RepliedChatMessageBannerProps> = ({
  className, onClose, repliedChatMessage,
}) => {
  const {compileChatMessageText} = useCompileChatMessageText()
  const compiledText = useMemo(
    () => compileChatMessageText(repliedChatMessage),
    [compileChatMessageText, repliedChatMessage],
  )
  const userDisplayName = useUserDisplayName()
  const isRepliedMessageAuthoredByCustomerUser =
    !!repliedChatMessage.authorUser.customerUser
  const {classes: styles} = useStyles({isRepliedMessageAuthoredByCustomerUser})
  const translate = useTranslate()
  const delayRemoveChatMessageIdSearchParam = useDelayRemoveChatMessageIdSearchParam()
  return (
    <Link
      className={`${styles.root} ${className ?? ''}`}
      onClick={() => {delayRemoveChatMessageIdSearchParam()}}
      to={`?chatMessageId=${repliedChatMessage.id}`}
    >
      {onClose && (
        <Box display="flex" justifyContent="end">
          <IconButton
            onClick={e => {
              e.stopPropagation()
              onClose(e)
            }}
            className={styles.closeIconButton} size="small"
          >
            <CloseIcon style={{alignSelf: 'end'}} size="small" />
          </IconButton>
        </Box>
      )}
      <div className={styles.content}>
        <Box maxWidth="80%">
          <Typography className={styles.authorUserName}>
            {isRepliedMessageAuthoredByCustomerUser ?
              userDisplayName(repliedChatMessage.authorUser) :
              translate(
                'chat.chatMessageBubble.repliedChatMessageBanner.' +
                'repliedMessageAuthoredByTheMerchantUserReading'
              )
            }
          </Typography>
          <Typography>
            <LineClamp>
              {compiledText ?? (
                !!repliedChatMessage.chatMessageFileAttachments.length &&
                repliedChatMessage.chatMessageFileAttachments[0].filename
              )}
            </LineClamp>
          </Typography>
        </Box>
        {!!repliedChatMessage.chatMessageFileAttachments.length && (
          <Box className={styles.fileAttachment}>
            <ChatMessageFileAttachment
              chatId={repliedChatMessage.chatId}
              isTranslationVisible={false}
              shouldDisplayFileName={false}
              shouldShowImagePreview={false}
              value={repliedChatMessage.chatMessageFileAttachments[0]}
            />
          </Box>
        )}
      </div>
    </Link>
  )
}

const useStyles = makeStyles<MakeStyleProps>()((
  theme, {isRepliedMessageAuthoredByCustomerUser}
) => ({
  authorUserName: {
    color: isRepliedMessageAuthoredByCustomerUser ? theme.palette.text.primary :
      theme.palette.info.main,
    paddingBottom: theme.remSpacing(0.5),
  },
  closeIconButton: {
    marginBottom: theme.remSpacing(1),
  },
  content: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
  },
  fileAttachment: {
    '& img': {
      maxHeight: theme.remSpacing(10),
    },
  },
  root: {
    backgroundColor: `rgba(${hexToRgb(theme.palette.disabled.main)},0.3)`,
    borderLeft: isRepliedMessageAuthoredByCustomerUser ?
      `6px solid ${theme.palette.secondary.dark}` :
      `6px solid ${theme.palette.info.main}`,
    borderRadius: '6px',
    color: theme.palette.text.secondary,
    columnGap: '0.5rem',
    cursor: 'pointer',
    display: 'block',
    height: "min-content",
    padding: theme.remSpacing(1),
    rowGap: '0.5rem',
    textDecoration: 'none !important',
  },
}))

interface RepliedChatMessageBannerProps {
  className?: string
  onClose?: DOMAttributes<HTMLButtonElement>['onClick']
  repliedChatMessage: ChatMessages
}

interface MakeStyleProps {
  isRepliedMessageAuthoredByCustomerUser?: boolean
}

export default RepliedChatMessageBanner
